// @ts-nocheck
// Read-only "Registration Details" panel for a single registrationTeam — mirrors
// the per-team detail shown on /admin/registration/:id (RegistrationAdmin.tsx) so
// the DivisionTeam page (/admin/team/:id) can surface the same payment / capacity /
// player info. Pure/presentational: all data comes via props (no store access), so
// it can be reused anywhere the enriched registrationTeam + payments + divisions
// are available.
import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Mail } from 'lucide-react';
import { CheckCircle2, Clock, AlertCircle, Lock } from 'lucide-react';
import helpers from '@/utils/helpers';
import { normEmail } from '@/utils/userProfileLookup';
import { entityLabel } from '@/utils/entityLabel';
import getTeamDisplayName from '@staty/shared/utils/getTeamDisplayName';
import {
  getPlayerWaiverStatus,
  calculateTotalPaymentMade,
  getPaymentStatus as getPaymentStatusShared,
} from '@staty/shared/utils/registrationStatus';
import { getWaitlistPosition } from '@staty/shared/utils/registrationCapacity';
import { getCapacityState, getRegistrationTeamsForDivision } from '@staty/shared/utils/waitlist';

const convertToUSD = (amount) => helpers.convertToUSD(Number(amount) || 0);

const DISCOUNT_LABELS = {
  earlyBird: 'Early Bird',
  lateRegistration: 'Late Registration',
  multiTeam: 'Multi-Team',
};

// Small key/value row used throughout the summary grid.
function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <div className="text-sm text-foreground">{children ?? <span className="text-muted-foreground/60">—</span>}</div>
    </div>
  );
}

export default function RegistrationTeamDetails({
  regTeam,
  payments = [],
  paymentPlans = [],
  registrationTeams = [],
  divisions = [],
  registration,
  profileFields = [],
  userProfileByUser = {},
  userProfileByEmail = {},
  playersActions = null,
  renderPlayerActions = null,
  onSendFinishRegistration = null,
}) {
  // Local selection of player emails for the "finish registration" bulk email.
  // Only active on the team page (where onSendFinishRegistration is passed).
  const [selectedEmails, setSelectedEmails] = React.useState(new Set());
  const [sendingFinish, setSendingFinish] = React.useState(false);
  if (!regTeam) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Registration Details</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No registration details for this team.</p>
        </CardContent>
      </Card>
    );
  }

  // ---- currency-aware payment helpers (mirror RegistrationAdmin closures) ----
  const sumPayments = (predicate) => {
    let amount = 0;
    _.each(_.filter(payments, predicate), (p) => {
      amount += (p.currency || 'usd').toLowerCase() === 'usd'
        ? p.amount
        : Math.round(p.amount * p.currencyConversionRate);
    });
    return amount;
  };
  const stripePaidCents = sumPayments({ registrationTeam: regTeam._id, donation: false });
  const donationCents = sumPayments({ registrationTeam: regTeam._id, donation: true });
  const madeCents = calculateTotalPaymentMade(Number(stripePaidCents), regTeam);
  const dueCents = typeof regTeam.resolvedCost === 'number' ? regTeam.resolvedCost : 0;

  const paymentBreakdown = () => {
    const rows = [];
    _.filter(payments, { registrationTeam: regTeam._id, donation: false }).forEach((p) => {
      const amount = (p.currency || 'usd').toLowerCase() === 'usd'
        ? p.amount
        : Math.round(p.amount * p.currencyConversionRate);
      rows.push({
        type: 'Online Payment',
        amount,
        date: p.paidDate,
        description: `Payment via ${p.provider || 'card'}`,
        paidByName: p.paidByName || null,
        paidByEmail: p.paidByEmail || null,
      });
    });
    if (regTeam.customPaidAmount && regTeam.customPaidAmount > 0) {
      rows.push({
        type: 'Manual Payment',
        amount: regTeam.customPaidAmount * 100,
        date: regTeam.customPaidDate || regTeam.paidDate || null,
        description: regTeam.paidNotes || 'Manual payment recorded by admin',
      });
    }
    return rows;
  };

  const playerAmountPaid = (email) => sumPayments((p) =>
    p.registrationTeam === regTeam._id
    && !p.donation
    && _.toLower(_.get(p, 'metadata.playerEmail', '')) === _.toLower(email || ''));

  const firstPayment = _.find(payments, { registrationTeam: regTeam._id, donation: false });
  const paymentDate = firstPayment ? moment(new Date(firstPayment.paidDate)).format('MM/DD/YYYY hh:mm a') : '';

  // ---- payment status badge ----
  const status = getPaymentStatusShared(dueCents, madeCents, regTeam.hasPaid);
  // INT-0018: the team's installment plan (if any) rides next to the status
  // badge — "Plan 2/3 · next Oct 10", amber when a charge is overdue.
  const teamPlan = _.find(paymentPlans, (pl) => String(pl.registrationTeam) === String(regTeam._id)
    && pl.status !== 'canceled');
  const planBadge = teamPlan ? (
    <Badge
      variant="outline"
      className={teamPlan.status === 'overdue'
        ? 'bg-warning/10 text-warning border-warning/30'
        : 'bg-muted/50 text-muted-foreground border-border'}
      title={teamPlan.lastError || undefined}
    >
      {teamPlan.status === 'overdue'
        ? `Plan overdue · ${teamPlan.chargedCount}/${teamPlan.count} paid`
        : `Plan ${teamPlan.chargedCount}/${teamPlan.count}${teamPlan.nextChargeAt
          ? ` · next ${moment(new Date(teamPlan.nextChargeAt)).format('MMM D')}`
          : ''}`}
    </Badge>
  ) : null;
  const statusBadge = (() => {
    switch (status) {
      case 'paid':
      case 'paid_manually':
        return <Badge variant="outline" className="bg-success/10 text-success border-success/30"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />{status === 'paid_manually' ? 'Paid (Manual)' : 'Paid'}</Badge>;
      case 'overpaid':
        return <Badge variant="outline" className="bg-info/10 text-info border-info/30"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Overpaid</Badge>;
      case 'partial':
        return <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30"><Clock className="w-3.5 h-3.5 mr-1" />Partial</Badge>;
      case 'free':
        return <Badge variant="outline" className="bg-muted/50 text-muted-foreground border-border">Free</Badge>;
      default:
        return <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30"><AlertCircle className="w-3.5 h-3.5 mr-1" />Unpaid</Badge>;
    }
  })();

  // ---- capacity / acceptance badge ----
  const divisionId = helpers.getObjId(regTeam.division) || helpers.getObjId(regTeam.requestedDivision);
  const teamDivision = _.find(divisions, (d) => String(d._id) === String(divisionId));
  const teamsInDivision = getRegistrationTeamsForDivision(divisionId, registrationTeams);
  const capacityState = teamDivision ? getCapacityState(regTeam, teamsInDivision, teamDivision) : null;
  const placementBadge = (() => {
    if (capacityState === 'waitlisted') {
      const pos = getWaitlistPosition(registrationTeams, regTeam._id, divisionId, helpers.getObjId(regTeam.registration), {
        division: teamDivision,
        getDivisionId: (t) => helpers.getObjId(t.division) || helpers.getObjId(t.requestedDivision),
        getRegistrationId: (t) => helpers.getObjId(t.registration),
      });
      return <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30">Waitlisted #{pos?.position || '?'}</Badge>;
    }
    if (regTeam.status) return <Badge variant="outline" className="bg-success/10 text-success border-success/30">{_.capitalize(regTeam.status)}</Badge>;
    return <Badge variant="outline" className="bg-muted/50 text-muted-foreground border-border">Registered</Badge>;
  })();

  const discounts = _.get(regTeam, 'appliedDiscounts', []) || [];
  const regFields = _.get(registration, 'fields', []) || [];
  const teamIsPlayerPay = !!(_.get(registration, 'paymentProduct.perPlayer') && _.get(registration, 'paymentProduct.playerPaySeparately'));
  const hasPlayerWaivers = (_.get(registration, 'playerWaivers', []) || []).length > 0;
  const players = _.get(regTeam, 'players', []) || [];
  const teamInfo = _.get(regTeam, 'info', {}) || {};
  const infoKeys = Object.keys(teamInfo).filter((k) => teamInfo[k] != null && teamInfo[k] !== '');

  // "Finish registration" per-player emailing is only offered on the team page
  // (onSendFinishRegistration passed) and only when players have something to
  // finish — a required waiver and/or their own payment share.
  const finishSelectable = !!onSendFinishRegistration && (hasPlayerWaivers || teamIsPlayerPay);
  const toggleEmail = (email) => setSelectedEmails((prev) => {
    const next = new Set(prev);
    if (next.has(email)) next.delete(email); else next.add(email);
    return next;
  });
  const handleSendFinish = async () => {
    if (!onSendFinishRegistration || selectedEmails.size === 0) return;
    setSendingFinish(true);
    try {
      await onSendFinishRegistration([...selectedEmails]);
      setSelectedEmails(new Set());
    } finally {
      setSendingFinish(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="flex items-center gap-2">
            Registration Details
            {_.get(regTeam, 'config.disableSync') && (
              <HoverCard>
                <HoverCardTrigger><Lock className="h-4 w-4 text-warning" /></HoverCardTrigger>
                <HoverCardContent className="text-sm">Team sync is disabled. Players cannot edit their information.</HoverCardContent>
              </HoverCard>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">{placementBadge}</div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Payment summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Payment Status">{statusBadge}
                {planBadge}</Field>
          <Field label="Payment Due">{convertToUSD(dueCents)}</Field>
          <Field label="Payment Made">
            <HoverCard>
              <HoverCardTrigger asChild>
                <button className="cursor-help border-b border-dashed border-muted-foreground/60 hover:border-muted-foreground">{convertToUSD(madeCents)}</button>
              </HoverCardTrigger>
              <HoverCardContent className="w-80">
                <div className="space-y-2">
                  <h4 className="font-medium leading-none">Payment Breakdown</h4>
                  {paymentBreakdown().length > 0 ? (
                    <div className="space-y-2 text-sm">
                      {paymentBreakdown().map((p, i) => (
                        <div key={i} className="flex justify-between items-start p-2 border rounded">
                          <div>
                            <div className="font-medium">{p.type}</div>
                            <div className="text-xs text-muted-foreground">{p.description}</div>
                            {(p.paidByName || p.paidByEmail) && (
                              <div className="text-xs text-muted-foreground">Paid by {p.paidByName || p.paidByEmail}</div>
                            )}
                            {p.date && <div className="text-xs text-muted-foreground">{moment(p.date).format('MM/DD/YYYY hh:mm A')}</div>}
                          </div>
                          <div className="font-medium">{convertToUSD(p.amount)}</div>
                        </div>
                      ))}
                      <div className="flex justify-between items-center pt-2 border-t">
                        <div className="font-medium">Total</div>
                        <div className="font-bold">{convertToUSD(madeCents)}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">No payments recorded</div>
                  )}
                </div>
              </HoverCardContent>
            </HoverCard>
          </Field>
          <Field label="Payment Date">{paymentDate}</Field>
          <Field label="Donations">{convertToUSD(donationCents)}</Field>
          <Field label="Discounts">
            {discounts.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {discounts.map((d) => (
                  <Badge key={d} variant="outline" className="bg-primary/10 text-primary border-primary/30">{DISCOUNT_LABELS[d] || d}</Badge>
                ))}
              </div>
            ) : null}
          </Field>
          {regTeam.paidNotes && <Field label="Payment Notes">{regTeam.paidNotes}</Field>}
        </div>

        <Separator />

        {/* Placement + registrant */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Division">
            {entityLabel(_.get(regTeam, 'division')) || '—'}
            {_.get(regTeam, 'requestedDivision.name') && helpers.getObjId(regTeam.division) !== helpers.getObjId(regTeam.requestedDivision) && (
              <div className="text-xs text-muted-foreground">Requested: {entityLabel(_.get(regTeam, 'requestedDivision'))}</div>
            )}
          </Field>
          <Field label="Format / Contest">
            {entityLabel(_.get(regTeam, 'contest')) || '—'}
            {_.get(regTeam, 'requestedContest.name') && helpers.getObjId(regTeam.contest) !== helpers.getObjId(regTeam.requestedContest) && (
              <div className="text-xs text-muted-foreground">Requested: {entityLabel(_.get(regTeam, 'requestedContest'))}</div>
            )}
          </Field>
          <Field label="Registrant">
            <div className="font-medium truncate" title={_.get(regTeam, 'user.name') || ''}>{_.get(regTeam, 'user.name') || '—'}</div>
            <div className="text-xs text-muted-foreground truncate" title={_.get(regTeam, 'user.email') || ''}>{_.get(regTeam, 'user.email')}</div>
          </Field>
          <Field label="Registered On">{regTeam.created ? moment(regTeam.created).format('MM/DD/YYYY hh:mm a') : ''}</Field>
          {infoKeys.map((k) => (
            <Field key={k} label={k}>{String(teamInfo[k])}</Field>
          ))}
        </div>

        {/* Players */}
        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">Players</span>
              <span className="text-xs text-muted-foreground">({players.length})</span>
            </div>
            <div className="flex items-center gap-2">
              {finishSelectable && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={selectedEmails.size === 0 || sendingFinish}
                  onClick={handleSendFinish}
                  title="Email the selected players a link to finish their registration (sign waiver / pay)"
                >
                  <Mail className="h-3.5 w-3.5 mr-1" />
                  {sendingFinish ? 'Sending…' : `Email selected to finish registration${selectedEmails.size ? ` (${selectedEmails.size})` : ''}`}
                </Button>
              )}
              {playersActions}
            </div>
          </div>
          {players.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {finishSelectable && <TableHead className="w-8" />}
                    {regFields.map((f) => <TableHead key={f.fieldId}>{f.name}</TableHead>)}
                    <TableHead>Email</TableHead>
                    {teamIsPlayerPay && <TableHead>Amount Paid</TableHead>}
                    {profileFields.map((f) => <TableHead key={`profile-${f.fieldId}`}>{`Profile: ${f.name}`}</TableHead>)}
                    {hasPlayerWaivers && <TableHead>Waiver</TableHead>}
                    {renderPlayerActions && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {players.map((player) => {
                    const email = _.get(player, 'info.email') || player.email || '';
                    const waiver = getPlayerWaiverStatus(player);
                    const up = userProfileByUser[helpers.getObjId(player.user)] || userProfileByEmail[normEmail(email)];
                    const emailLc = email.trim().toLowerCase();
                    return (
                      <TableRow key={player._id}>
                        {finishSelectable && (
                          <TableCell className="w-8">
                            {emailLc ? (
                              <Checkbox
                                checked={selectedEmails.has(emailLc)}
                                onCheckedChange={() => toggleEmail(emailLc)}
                                aria-label={`Select ${email}`}
                              />
                            ) : null}
                          </TableCell>
                        )}
                        {regFields.map((f) => (
                          <TableCell key={player._id + f.fieldId}>{_.get(player, ['info', f.fieldId])}</TableCell>
                        ))}
                        <TableCell className="max-w-[220px] truncate" title={email}>{email}</TableCell>
                        {teamIsPlayerPay && <TableCell>{convertToUSD(playerAmountPaid(email))}</TableCell>}
                        {profileFields.map((f) => {
                          const v = _.get(up, ['info', f.fieldId]);
                          return (
                            <TableCell key={player._id + 'profile-' + f.fieldId} className="max-w-[220px] truncate" title={String(v ?? '')}>{v}</TableCell>
                          );
                        })}
                        {hasPlayerWaivers && (
                          <TableCell>
                            {waiver === 'signed' ? (
                              <span className="inline-flex items-center text-success text-sm"><CheckCircle2 className="h-4 w-4 mr-1" />Signed</span>
                            ) : (
                              <span className="inline-flex items-center text-warning text-sm"><Clock className="h-4 w-4 mr-1" />Pending</span>
                            )}
                          </TableCell>
                        )}
                        {renderPlayerActions && (
                          // `amountPaid` comes from the SAME helper that fills the
                          // "Amount Paid" column above, so a remove confirmation
                          // can't quote a different number than the table shows.
                          <TableCell className="text-right">{renderPlayerActions(player, { amountPaid: playerAmountPaid(email) })}</TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No players on this team.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
